import { useSyncExternalStore } from 'react'

/**
 * Tag picks staged from an ambiguity card, keyed by workspace tab. An
 * identifier may hold several picks. Staged picks ride the next typed message,
 * or "Apply selection" sends them as their own turn; either way they never
 * appear as composer text. Staged picks persist to sessionStorage; once
 * consumed, the turn's tagSelections (hiddenContext on older turns) is the
 * record the card reads back.
 */
export interface StagedTagPick {
  identifier: string
  entryId: string
  label: string
}

const DEFAULT_TAB_KEY = 'default'
const STORAGE_KEY = 'docChatTagPicks:v2'

const listeners = new Set<() => void>()
/** Immutable snapshots per tab so useSyncExternalStore sees stable values. */
const snapshotByTab = new Map<string, StagedTagPick[]>()
const picksByTab = loadPersistedPicks()

const EMPTY_PICKS: StagedTagPick[] = []

const LABEL_LIST = new Intl.ListFormat('en', { type: 'conjunction' })

function pickKey(pick: Pick<StagedTagPick, 'identifier' | 'entryId'>): string {
  return `${pick.identifier}\u0000${pick.entryId}`
}

function loadPersistedPicks(): Map<string, Map<string, StagedTagPick>> {
  const byTab = new Map<string, Map<string, StagedTagPick>>()
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return byTab
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return byTab
    Object.entries(parsed).forEach(([key, picks]) => {
      if (!Array.isArray(picks)) return
      const map = new Map<string, StagedTagPick>()
      picks.forEach((pick: unknown) => {
        if (
          typeof pick === 'object' &&
          pick !== null &&
          'identifier' in pick &&
          'entryId' in pick &&
          'label' in pick &&
          typeof pick.identifier === 'string' &&
          typeof pick.entryId === 'string' &&
          typeof pick.label === 'string'
        ) {
          const staged = {
            identifier: pick.identifier,
            entryId: pick.entryId,
            label: pick.label,
          }
          map.set(pickKey(staged), staged)
        }
      })
      if (map.size > 0) {
        byTab.set(key, map)
        snapshotByTab.set(key, [...map.values()])
      }
    })
  } catch {
    /* sessionStorage unavailable or corrupt: start empty */
  }
  return byTab
}

function persistPicks(): void {
  try {
    if (picksByTab.size === 0) {
      sessionStorage.removeItem(STORAGE_KEY)
      return
    }
    const payload: Record<string, StagedTagPick[]> = {}
    picksByTab.forEach((picks, key) => {
      if (picks.size > 0) payload[key] = [...picks.values()]
    })
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* sessionStorage unavailable: picks stay in memory for this session */
  }
}

function tabKey(tabId: string | undefined): string {
  return tabId ?? DEFAULT_TAB_KEY
}

function refreshSnapshot(key: string): void {
  const picks = picksByTab.get(key)
  if (!picks || picks.size === 0) {
    snapshotByTab.delete(key)
  } else {
    snapshotByTab.set(key, [...picks.values()])
  }
  persistPicks()
  listeners.forEach((listener) => listener())
}

export function stageTagPick(
  tabId: string | undefined,
  pick: StagedTagPick
): void {
  const key = tabKey(tabId)
  const picks = picksByTab.get(key) ?? new Map<string, StagedTagPick>()
  picks.set(pickKey(pick), pick)
  picksByTab.set(key, picks)
  refreshSnapshot(key)
}

export function unstageTagPick(
  tabId: string | undefined,
  pick: Pick<StagedTagPick, 'identifier' | 'entryId'>
): void {
  const key = tabKey(tabId)
  picksByTab.get(key)?.delete(pickKey(pick))
  refreshSnapshot(key)
}

export function resetStagedTagPicks(): void {
  picksByTab.clear()
  snapshotByTab.clear()
  persistPicks()
  listeners.forEach((listener) => listener())
}

export function consumeTagPicks(tabId: string | undefined): StagedTagPick[] {
  const key = tabKey(tabId)
  const picks = snapshotByTab.get(key) ?? EMPTY_PICKS
  picksByTab.delete(key)
  refreshSnapshot(key)
  return picks
}

export function tagPicksHiddenContext(picks: StagedTagPick[]): string {
  const labelsByIdentifier = new Map<string, string[]>()
  picks.forEach((pick) => {
    const labels = labelsByIdentifier.get(pick.identifier) ?? []
    labelsByIdentifier.set(pick.identifier, [...labels, pick.label])
  })
  return [...labelsByIdentifier]
    .map(([identifier, labels]) => {
      // eslint-disable-next-line lingui/no-unlocalized-strings -- Persisted model prompt syntax is parsed by PICK_LINE_PATTERN below.
      return `For "${identifier}", use ${LABEL_LIST.format(labels)}.`
    })
    .join('\n')
}

const PICK_LINE_PATTERN = /^For ".+", use .+\.$/

/**
 * Undo docChatTurnRequestQuery on a stored prompt: peel a trailing block of
 * pick lines back off so reopened history never renders hidden context.
 */
export function splitComposedDocChatQuery(composed: string): {
  query: string
  hiddenContext?: string
} {
  const blocks = composed.split('\n\n')
  const last = blocks.at(-1)
  if (
    blocks.length < 2 ||
    last === undefined ||
    !last.split('\n').every((line) => PICK_LINE_PATTERN.test(line))
  ) {
    return { query: composed }
  }
  return { query: blocks.slice(0, -1).join('\n\n'), hiddenContext: last }
}

const LABEL_LIST_SEPARATORS = [
  ...new Set(
    [
      ['a', 'b'],
      ['a', 'b', 'c'],
    ].flatMap((labels) =>
      LABEL_LIST.formatToParts(labels)
        .filter((part) => part.type === 'literal')
        .map((part) => part.value)
    )
  ),
].sort((a, b) => b.length - a.length)

function parseLabelList(
  listed: string,
  candidateLabels: string[]
): string[] | null {
  if (listed === '') return []
  for (const label of candidateLabels) {
    if (!listed.startsWith(label)) continue
    const rest = listed.slice(label.length)
    if (rest === '') return [label]
    for (const separator of LABEL_LIST_SEPARATORS) {
      if (!rest.startsWith(separator)) continue
      const tail = parseLabelList(rest.slice(separator.length), candidateLabels)
      if (tail) return [label, ...tail]
    }
  }
  return null
}

/**
 * Read consumed picks back out of a turn's hidden context. Labels are free
 * text and may contain the list's own separators, so the list is read as a
 * sequence of whole candidate labels; a line that does not parse restores
 * nothing.
 */
export function pickedLabelsFromHiddenContext(
  hiddenContext: string | undefined,
  identifier: string,
  candidateLabels: string[]
): string[] {
  if (!hiddenContext) return []
  // eslint-disable-next-line lingui/no-unlocalized-strings -- Must match persisted prompt syntax in tagPicksHiddenContext.
  const prefix = `For "${identifier}", use `
  for (const line of hiddenContext.split('\n')) {
    if (line.startsWith(prefix) && line.endsWith('.')) {
      return (
        parseLabelList(line.slice(prefix.length, -1), candidateLabels) ?? []
      )
    }
  }
  return []
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useStagedTagPicks(tabId: string | undefined): StagedTagPick[] {
  return useSyncExternalStore(
    subscribe,
    () => snapshotByTab.get(tabKey(tabId)) ?? EMPTY_PICKS,
    () => EMPTY_PICKS
  )
}
